/**
 * Buildings energy efficiency — stock-based model when data allow, else segment multipliers on pathway totals.
 *
 * Stock model (housing + establishments from tool / Supabase):
 * - Existing 2023 residential/commercial cohort: fixed counts; 2023 intensity per unit; optional BAU efficiency ramp.
 * - New residential: all-electric; grid load = NEW_RESIDENTIAL_ENERGY_FRAC_OF_EXISTING × (existing kWh-eq/home)
 *   × (1 − solar offset); reach-code lever applies only to new residential.
 * - New commercial: all-electric; same fractional rule vs existing establishment intensity.
 * - Policy retrofits: separate linear ramps on existing residential vs existing commercial demand.
 *
 * Order in emissions pipeline: BAU pathway → this layer → electrification → RNG/grid (demand response removed).
 */

import type { EnergyEfficiencyComponents } from '../types/policyImpact';
import { normalizeEnergyEfficiencyComponents, hasAnyEfficiencyComponent } from '../types/policyImpact';

const BASE_YEAR = 2023;
const TARGET_YEAR = 2045;

/** 1 therm = 29.3 kWh; used to aggregate therms + kWh into a single energy quantity. */
const THERM_TO_KWH = 29.3;

/** New residential/commercial units: site energy as this fraction of an existing unit (kWh-equivalent), all-electric new stock. */
export const NEW_STOCK_ENERGY_FRAC_OF_EXISTING = 0.33;

/** Share of new all-electric building load met on-site (solar); reduces grid kWh. */
export const NEW_BUILDING_SOLAR_GRID_REDUCTION = 0.35;

/**
 * Commercial stock growth when no establishment series (fallback existing share only).
 * @deprecated Prefer establishment counts from Supabase.
 */
export const COMMERCIAL_ANNUAL_GROWTH_RATE = 0.005;

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

/** Inputs for housing/establishment stock efficiency (from compact development + buildings baseline). */
export interface StockModelInputs {
  housingUnits2023: number;
  housingUnitsByYear: (year: number) => number;
  resTherms2023: number;
  resKwh2023: number;
  comTherms2023: number;
  comKwh2023: number;
  establishments2023: number;
  establishmentsByYear: (year: number) => number;
  bauExistingEfficiencyPercent2045: number;
  applyBauExistingEfficiency: boolean;
}

export interface EfficiencyLayerOptions {
  resFractionTherms?: number;
  resFractionKwh?: number;
  existingResShareByYear?: (year: number) => number;
  existingComShareByYear?: (year: number) => number;
  stockModel?: StockModelInputs;
}

function defaultExistingResShare(year: number): number {
  if (year <= BASE_YEAR) return 1;
  const growth = 0.02;
  const yearsOut = year - BASE_YEAR;
  const unitsAtYear = Math.pow(1 + growth, yearsOut);
  return Math.max(0, Math.min(1, 1 / unitsAtYear));
}

function defaultExistingComShare(year: number): number {
  if (year <= BASE_YEAR) return 1;
  const yearsOut = year - BASE_YEAR;
  const stockIndex = Math.pow(1 + COMMERCIAL_ANNUAL_GROWTH_RATE, yearsOut);
  return 1 / stockIndex;
}

function rampFraction(year: number): number {
  if (year <= BASE_YEAR) return 0;
  if (year >= TARGET_YEAR) return 1;
  return (year - BASE_YEAR) / (TARGET_YEAR - BASE_YEAR);
}

function applyEfficiencyStockModel(
  year: number,
  components: EnergyEfficiencyComponents,
  sm: StockModelInputs
): { thermsAfter: number; kwhAfter: number } {
  const ramp = rampFraction(year);
  const U0 = Math.max(0, sm.housingUnits2023);
  const Uy = Math.max(0, sm.housingUnitsByYear(year));
  const E0 = Math.max(0, sm.establishments2023);
  const Ey = Math.max(0, sm.establishmentsByYear(year));

  const resTperH = U0 > 0 ? sm.resTherms2023 / U0 : 0;
  const resKperH = U0 > 0 ? sm.resKwh2023 / U0 : 0;
  const comTperE = E0 > 0 ? sm.comTherms2023 / E0 : 0;
  const comKperE = E0 > 0 ? sm.comKwh2023 / E0 : 0;

  const bauImpr =
    sm.applyBauExistingEfficiency && sm.bauExistingEfficiencyPercent2045 > 0
      ? (sm.bauExistingEfficiencyPercent2045 / 100) * ramp
      : 0;
  const bauExistingMult = 1 - bauImpr;

  const retroResMult = 1 - (components.residentialRetrofitExistingPercent / 100) * ramp;
  const retroComMult = 1 - (components.commercialRetrofitExistingPercent / 100) * ramp;
  const otherMult = 1 - (components.otherEfficiencyPercent / 100) * ramp;
  const reachMult = 1 - (components.residentialReachCodePercent / 100) * ramp;

  const newHomes = Math.max(0, Uy - U0);
  const newEst = Math.max(0, Ey - E0);

  const existingHomeThermEq = resTperH * THERM_TO_KWH + resKperH;
  const solarNet = 1 - NEW_BUILDING_SOLAR_GRID_REDUCTION;

  let existingResTherms = U0 * resTperH * bauExistingMult * retroResMult * otherMult;
  let existingResKwh = U0 * resKperH * bauExistingMult * retroResMult * otherMult;

  let newResTherms = 0;
  let newResKwh =
    newHomes > 0
      ? newHomes * NEW_STOCK_ENERGY_FRAC_OF_EXISTING * existingHomeThermEq * solarNet * reachMult * otherMult
      : 0;

  let existingComTherms = E0 * comTperE * bauExistingMult * retroComMult * otherMult;
  let existingComKwh = E0 * comKperE * bauExistingMult * retroComMult * otherMult;

  const existingEstThermEq = comTperE * THERM_TO_KWH + comKperE;
  let newComTherms = 0;
  let newComKwh =
    newEst > 0
      ? newEst * NEW_STOCK_ENERGY_FRAC_OF_EXISTING * existingEstThermEq * solarNet * otherMult
      : 0;

  let thermsAfter = existingResTherms + newResTherms + existingComTherms + newComTherms;
  let kwhAfter = existingResKwh + newResKwh + existingComKwh + newComKwh;

  thermsAfter = Math.max(0, thermsAfter);
  kwhAfter = Math.max(0, kwhAfter);

  return { thermsAfter, kwhAfter };
}

/** Scale stock-model demand to match pathway BAU totals for the year (conservation of citywide scale). */
function scaleStockToPathwayBau(
  pol: { thermsAfter: number; kwhAfter: number },
  base: { thermsAfter: number; kwhAfter: number },
  pathwayTherms: number,
  pathwayKwh: number
): { thermsAfter: number; kwhAfter: number } {
  const pathwayComb = pathwayTherms * THERM_TO_KWH + pathwayKwh;
  const baseComb = base.thermsAfter * THERM_TO_KWH + base.kwhAfter;
  if (baseComb <= 0 || pathwayComb <= 0) return pol;
  const scale = pathwayComb / baseComb;
  return {
    thermsAfter: pol.thermsAfter * scale,
    kwhAfter: pol.kwhAfter * scale,
  };
}

function applyEfficiencyLegacySegment(
  bauTherms: number,
  bauKwh: number,
  year: number,
  components: EnergyEfficiencyComponents,
  options?: EfficiencyLayerOptions
): { thermsAfter: number; kwhAfter: number; efficiencyPercentTherms: number; efficiencyPercentKwh: number } {
  const ramp = rampFraction(year);
  const resFracT = clamp(options?.resFractionTherms ?? 0.6, 0, 1);
  const resFracK = options?.resFractionKwh ?? resFracT;
  const existingRes = options?.existingResShareByYear?.(year) ?? defaultExistingResShare(year);
  const existingCom = options?.existingComShareByYear?.(year) ?? defaultExistingComShare(year);

  const newResShare = 1 - existingRes;
  const newComShare = 1 - existingCom;

  const resTherms = bauTherms * resFracT;
  const comTherms = bauTherms * (1 - resFracT);
  const resKwh = bauKwh * resFracK;
  const comKwh = bauKwh * (1 - resFracK);

  const existingResTherms = resTherms * existingRes;
  const newResTherms = resTherms * newResShare;
  const existingComTherms = comTherms * existingCom;
  const newComTherms = comTherms * newComShare;
  const existingResKwh = resKwh * existingRes;
  const newResKwh = resKwh * newResShare;
  const existingComKwh = comKwh * existingCom;
  const newComKwh = comKwh * newComShare;

  const resReachMult = 1 - (components.residentialReachCodePercent / 100) * ramp;
  const retroResMult = 1 - (components.residentialRetrofitExistingPercent / 100) * ramp;
  const retroComMult = 1 - (components.commercialRetrofitExistingPercent / 100) * ramp;
  const otherMult = 1 - (components.otherEfficiencyPercent / 100) * ramp;

  const existingResThermsAfter = existingResTherms * retroResMult * otherMult;
  const newResThermsAfter = newResTherms * resReachMult * otherMult;
  const existingComThermsAfter = existingComTherms * retroComMult * otherMult;
  const newComThermsAfter = newComTherms * otherMult;

  const existingResKwhAfter = existingResKwh * retroResMult * otherMult;
  const newResKwhAfter = newResKwh * resReachMult * otherMult;
  const existingComKwhAfter = existingComKwh * retroComMult * otherMult;
  const newComKwhAfter = newComKwh * otherMult;

  let thermsAfter =
    existingResThermsAfter + newResThermsAfter + existingComThermsAfter + newComThermsAfter;
  let kwhAfter =
    existingResKwhAfter + newResKwhAfter + existingComKwhAfter + newComKwhAfter;

  thermsAfter = Math.max(0, thermsAfter);
  kwhAfter = Math.max(0, kwhAfter);

  const efficiencyPercentTherms =
    bauTherms > 0 ? (1 - thermsAfter / bauTherms) * 100 : 0;
  const efficiencyPercentKwh =
    bauKwh > 0 ? (1 - kwhAfter / bauKwh) * 100 : 0;

  return {
    thermsAfter,
    kwhAfter,
    efficiencyPercentTherms: clamp(efficiencyPercentTherms, 0, 100),
    efficiencyPercentKwh: clamp(efficiencyPercentKwh, 0, 100),
  };
}

export function applyEfficiencyForYear(
  bauTherms: number,
  bauKwh: number,
  year: number,
  components: EnergyEfficiencyComponents,
  options?: EfficiencyLayerOptions
): { thermsAfter: number; kwhAfter: number; efficiencyPercentTherms: number; efficiencyPercentKwh: number } {
  const c = normalizeEnergyEfficiencyComponents(components);
  const sm = options?.stockModel;
  // Stock reconstruction + scaleStockToPathwayBau preserves combined energy but can shift gas vs electricity vs
  // the BAU pathway. When no named levers and no BAU-existing improvement is active, return the pathway unchanged
  // so charts and "0% policy" match BAU line-by-line.
  if (
    sm &&
    sm.housingUnits2023 > 0 &&
    sm.establishments2023 > 0 &&
    !hasAnyEfficiencyComponent(c) &&
    !(sm.applyBauExistingEfficiency && (sm.bauExistingEfficiencyPercent2045 ?? 0) > 0)
  ) {
    return {
      thermsAfter: bauTherms,
      kwhAfter: bauKwh,
      efficiencyPercentTherms: 0,
      efficiencyPercentKwh: 0,
    };
  }
  if (sm && sm.housingUnits2023 > 0 && sm.establishments2023 > 0) {
    const zero = normalizeEnergyEfficiencyComponents({
      residentialReachCodePercent: 0,
      residentialRetrofitExistingPercent: 0,
      commercialRetrofitExistingPercent: 0,
      otherEfficiencyPercent: 0,
    });
    const baseStock = applyEfficiencyStockModel(year, zero, sm);
    const polStock = applyEfficiencyStockModel(year, c, sm);
    const scaled = scaleStockToPathwayBau(polStock, baseStock, bauTherms, bauKwh);
    const thermsAfter = scaled.thermsAfter;
    const kwhAfter = scaled.kwhAfter;
    return {
      thermsAfter,
      kwhAfter,
      efficiencyPercentTherms:
        bauTherms > 0 ? clamp((1 - thermsAfter / bauTherms) * 100, 0, 100) : 0,
      efficiencyPercentKwh: bauKwh > 0 ? clamp((1 - kwhAfter / bauKwh) * 100, 0, 100) : 0,
    };
  }
  return applyEfficiencyLegacySegment(bauTherms, bauKwh, year, c, options);
}

/** Marginal policy contributions (therm-equivalent kWh vs full policy). */
export interface EfficiencyContributions {
  residentialRetrofit: number;
  commercialRetrofit: number;
  residentialReach: number;
  other: number;
}

function componentsWithPolicyZeroed(
  components: EnergyEfficiencyComponents,
  key: keyof EnergyEfficiencyComponents
): EnergyEfficiencyComponents {
  return { ...components, [key]: 0 };
}

export function applyEfficiencyForYearWithContributions(
  bauTherms: number,
  bauKwh: number,
  year: number,
  components: EnergyEfficiencyComponents,
  options?: EfficiencyLayerOptions
): {
  thermsAfter: number;
  kwhAfter: number;
  contributions: EfficiencyContributions;
} {
  const c = normalizeEnergyEfficiencyComponents(components);
  const full = applyEfficiencyForYear(bauTherms, bauKwh, year, c, options);
  const combinedAfter = full.thermsAfter * THERM_TO_KWH + full.kwhAfter;

  const keys: (keyof EnergyEfficiencyComponents)[] = [
    'residentialRetrofitExistingPercent',
    'commercialRetrofitExistingPercent',
    'residentialReachCodePercent',
    'otherEfficiencyPercent',
  ];
  const contributionKeys: (keyof EfficiencyContributions)[] = [
    'residentialRetrofit',
    'commercialRetrofit',
    'residentialReach',
    'other',
  ];
  const contributions: EfficiencyContributions = {
    residentialRetrofit: 0,
    commercialRetrofit: 0,
    residentialReach: 0,
    other: 0,
  };
  for (let i = 0; i < keys.length; i++) {
    const k = keys[i];
    const ck = contributionKeys[i];
    if ((c[k] ?? 0) === 0) continue;
    const zeroed = normalizeEnergyEfficiencyComponents(componentsWithPolicyZeroed(c, k));
    const without = applyEfficiencyForYear(bauTherms, bauKwh, year, zeroed, options);
    const combinedWithout = without.thermsAfter * THERM_TO_KWH + without.kwhAfter;
    contributions[ck] = Math.max(0, combinedWithout - combinedAfter);
  }
  return {
    thermsAfter: full.thermsAfter,
    kwhAfter: full.kwhAfter,
    contributions,
  };
}

export interface EfficiencyLayerResult {
  thermsByYear: number[];
  kwhByYear: number[];
  years: number[];
  totalEfficiencyPercent2045: number;
}

export function applyEfficiencyLayer(
  bauThermsByYear: number[],
  bauKwhByYear: number[],
  years: number[],
  components: EnergyEfficiencyComponents,
  options?: EfficiencyLayerOptions
): EfficiencyLayerResult {
  const thermsByYear = years.map((year, i) => {
    const bauT = bauThermsByYear[i] ?? 0;
    const bauK = bauKwhByYear[i] ?? 0;
    return applyEfficiencyForYear(bauT, bauK, year, components, options).thermsAfter;
  });
  const kwhByYear = years.map((year, i) => {
    const bauT = bauThermsByYear[i] ?? 0;
    const bauK = bauKwhByYear[i] ?? 0;
    return applyEfficiencyForYear(bauT, bauK, year, components, options).kwhAfter;
  });

  const idx2045 = years.indexOf(TARGET_YEAR);
  const bauT2045 = idx2045 >= 0 ? bauThermsByYear[idx2045] ?? 0 : 0;
  const bauK2045 = idx2045 >= 0 ? bauKwhByYear[idx2045] ?? 0 : 0;
  const out2045 = applyEfficiencyForYear(bauT2045, bauK2045, TARGET_YEAR, components, options);
  const combinedBefore = bauT2045 * THERM_TO_KWH + bauK2045;
  const combinedAfter = out2045.thermsAfter * THERM_TO_KWH + out2045.kwhAfter;
  const totalEfficiencyPercent2045 =
    combinedBefore > 0 ? (1 - combinedAfter / combinedBefore) * 100 : 0;

  return {
    thermsByYear,
    kwhByYear,
    years,
    totalEfficiencyPercent2045: clamp(totalEfficiencyPercent2045, 0, 100),
  };
}

export function computeTotalEfficiencyPercentFromComponents(
  components: EnergyEfficiencyComponents,
  bauTherms2045: number,
  bauKwh2045: number,
  options?: EfficiencyLayerOptions
): number {
  const out = applyEfficiencyForYear(
    bauTherms2045,
    bauKwh2045,
    TARGET_YEAR,
    components,
    options
  );
  const combinedBefore = bauTherms2045 * THERM_TO_KWH + bauKwh2045;
  const combinedAfter = out.thermsAfter * THERM_TO_KWH + out.kwhAfter;
  if (combinedBefore <= 0) return 0;
  return clamp((1 - combinedAfter / combinedBefore) * 100, 0, 100);
}

const OTHER_EFFICIENCY_CAP = 40;

export function solveOtherEfficiencyPercentForCombinedTarget(
  targetPercent: number,
  named: Pick<
    EnergyEfficiencyComponents,
    | 'residentialReachCodePercent'
    | 'residentialRetrofitExistingPercent'
    | 'commercialRetrofitExistingPercent'
  >,
  bauTherms2045: number,
  bauKwh2045: number,
  options?: EfficiencyLayerOptions
): { otherEfficiencyPercent: number; achievableTarget: number } {
  const base = normalizeEnergyEfficiencyComponents({
    ...named,
    otherEfficiencyPercent: 0,
  });

  const totalAt = (other: number) =>
    computeTotalEfficiencyPercentFromComponents(
      { ...base, otherEfficiencyPercent: clamp(other, 0, OTHER_EFFICIENCY_CAP) },
      bauTherms2045,
      bauKwh2045,
      options
    );

  const combinedBefore = bauTherms2045 * THERM_TO_KWH + bauKwh2045;
  if (combinedBefore <= 0) {
    return { otherEfficiencyPercent: 0, achievableTarget: 0 };
  }

  const t0 = totalAt(0);
  const tMax = totalAt(OTHER_EFFICIENCY_CAP);
  if (tMax < t0 - 1e-6) {
    return { otherEfficiencyPercent: 0, achievableTarget: t0 };
  }

  const target = clamp(targetPercent, 0, 100);

  if (target <= t0 + 1e-4) {
    return { otherEfficiencyPercent: 0, achievableTarget: t0 };
  }
  if (target >= tMax - 1e-4) {
    return { otherEfficiencyPercent: OTHER_EFFICIENCY_CAP, achievableTarget: tMax };
  }

  let lo = 0;
  let hi = OTHER_EFFICIENCY_CAP;
  for (let i = 0; i < 45; i++) {
    const mid = (lo + hi) / 2;
    if (totalAt(mid) < target) lo = mid;
    else hi = mid;
  }
  const otherEfficiencyPercent = clamp(hi, 0, OTHER_EFFICIENCY_CAP);
  const achievableTarget = totalAt(otherEfficiencyPercent);
  return { otherEfficiencyPercent, achievableTarget };
}
